import React, { useState, useEffect, useRef } from "react";
import ReactPlayer from "react-player";
import PosterPreview from "./PosterPreview";
import Button from "react-bootstrap/Button";
import PosterGallery from "./PosterGallery";
import { SESSIONS } from "./config";
import Form from "react-bootstrap/Form";
import { Typeahead } from "react-bootstrap-typeahead";
import useLocalStorage from "./hooks/useLocalStorage";
import AlertDismissible from "./AlertDismissible";
import { Play, Stop } from "react-bootstrap-icons";

const sortPostersByTitleAZ = (a, b) => {
  let nameA = a.title.toUpperCase();
  let nameB = b.title.toUpperCase();
  if (nameA < nameB) {
    return -1;
  } else if (nameA > nameB) {
    return 1;
  } else {
    return 0;
  }
};

const filterPosters = (poster, filterArray) => {
  if (filterArray.length === 0) {
    return false;
  }

  if (filterArray.length === 1 && filterArray[0] === "all") {
    return true;
  }

  return filterArray.includes(poster.id);
};

const AllPosters = ({ config, header = null }) => {
  const playerRef = useRef();
  const [isRecordingPlaying, setIsRecordingPlaying] = useState(false);
  const [showInstructions, setShowInstructions] = useLocalStorage(
    "posters-instructions",
    true
  );

  const [currentPoster, setCurrentPoster] = useState(-1);
  const [showSessions, setShowSessions] = useState(
    SESSIONS.split(",").reduce(function (acc, cur) {
      acc[cur] = cur;
      return acc;
    }, {})
  );
  const [postersInTopicFilter, setPostersInTopicFilter] = useState(["all"]);
  const sessions = Object.values(config.themes || []).filter((session) => {
    return SESSIONS.split(",").map(x => {
      if (isNaN(x)) {
        return x;
      } else {
        return Number(x)
      }
    }).includes(session.id)
  }
  );
  const posters = sessions
    .filter((s) => showSessions[s.id])
    .map((x) => Object.values(x.posters))
    .flat();

  const searchOptions = sessions
    .filter((s) => showSessions[s.id])
    .map((x) => Object.values(x.posters))
    .flat()
    .sort(sortPostersByTitleAZ)
    .map((x, idx) => [
      { id: x.id, value: x.authors, idx, type: "posterId" },
      { id: x.id, value: x.title, idx, type: "posterId" },
    ])
    .flat();

  const topics = Object.values(config.topics || {}).map((t) => ({
    value: t.title,
    posters: Object.keys(t.posters),
    type: "topic",
  }));

  useEffect(() => {
    if (currentPoster === -1) {
      setPostersInTopicFilter(["all"]);
    }
  }, [currentPoster]);

  if (currentPoster === -1)
    return (
      <div className="poster-session sessions-with-sidebar">
        {header && header}
        <div className="sidebar">
          <Form>
            <Form.Group>
              <Typeahead
                id="basic-typeahead-single"
                minLength={3}
                labelKey="value"
                onChange={(e) => {
                  if (!e || !e[0]) {
                    setPostersInTopicFilter(["all"]);
                    return;
                  }
                  if (e[0].type === "posterId") {
                    setCurrentPoster(e[0].idx);
                  } else if (e[0].type === "topic") {
                    setPostersInTopicFilter(e[0].posters);
                  }
                }}
                options={[...searchOptions, ...topics]}
                placeholder="Search abstract, topic or author..."
                emptyLabel="No abstracts"
              />
            </Form.Group>
          </Form>
          <Form>
            <Form.Label>
              <b>Abstracts</b>
            </Form.Label>
            <div className="sessions-checkboxes">
              {config.themes &&
                Object.values(config.themes).map((theme) => {
                  return (
                    <Form.Check key={theme.id}>
                      <Form.Check.Input
                        defaultChecked={!!showSessions[theme.id]}
                        onClick={() => {
                          if (!showSessions[theme.id]) {
                            showSessions[theme.id] = theme.id;
                          } else {
                            delete showSessions[theme.id];
                          }
                          setShowSessions({ ...showSessions });
                        }}
                        id={theme.id}
                        type="checkbox"
                      />
                      <Form.Check.Label htmlFor={theme.id}>
                        {`${theme.title} (${Object.keys(theme.posters).length})`}
                      </Form.Check.Label>
                    </Form.Check>
                  );
                })}
            </div>
          </Form>
        </div>
        <div className="session-posters wrap">
          {showInstructions && config.instructions && (
            <AlertDismissible
              show={showInstructions}
              config={config.instructions}
              setShowInstructions={setShowInstructions}
            />
          )}
          {posters
            .filter((poster) => filterPosters(poster, postersInTopicFilter))
            .sort(sortPostersByTitleAZ)
            .map((poster, idx) => (
              <div className="flex-item" key={poster.id}>
                <PosterPreview
                  poster={poster}
                  onClick={() => setCurrentPoster(idx)}
                />
              </div>
            ))}
        </div>
      </div>
    );

  const poster = posters
    .filter((poster) => filterPosters(poster, postersInTopicFilter))
    .sort(sortPostersByTitleAZ)[currentPoster];

  const recordingUrl = poster.pages.find(
    (x) => x.indexOf("mp3") > -1 || x.indexOf("mp4") > -1
  );

  return (
    <div className="poster-session scroll">
      <Button className="back-button" onClick={() => setCurrentPoster(-1)}>
        <i className="fas fa-arrow-left" />
      </Button>
      <div className="title-with-buttons">
        <h2 className="poster-title">{poster.title}</h2>
        <p>{poster.authors}</p>
        {poster.contactDetails && (
          <div className="contact-details">
            {poster.contactDetails.fullName && (
              <div className="contact-details-item">{`Contact: ${poster.contactDetails.fullName}`}</div>
            )}
            {poster.contactDetails.email && (
              <div className="contact-details-item">
                Email:{" "}
                <a href={`mailto:${poster.contactDetails.email}`}>
                  {poster.contactDetails.email}
                </a>{" "}
              </div>
            )}
            {poster.contactDetails.skype && (
              <div className="contact-details-item">{`Skype: ${poster.contactDetails.skype}`}</div>
            )}
          </div>
        )}
        {recordingUrl && (
          <>
            <div>
              <Button
                className="playback-button"
                onClick={() => setIsRecordingPlaying((s) => !s)}
              >
                {isRecordingPlaying ? (
                  <>
                    <Stop className="playback-icon" /> Zatrzymaj
                  </>
                ) : (
                  <>
                    <Play className="playback-icon" /> Listen to presentation
                    Autora
                  </>
                )}
              </Button>
            </div>
            <ReactPlayer
              ref={playerRef}
              url={recordingUrl}
              width="0px"
              height="0px"
              playing={isRecordingPlaying}
            />
          </>
        )}
        <div style={{ padding: "1rem" }}>
          <Button
            variant={currentPoster === 0 ? "dark" : "primary"}
            disabled={currentPoster === 0}
            onClick={() => setCurrentPoster((current) => current - 1)}
          >
            Previous abstract
          </Button>
          <Button
            variant={
              currentPoster ===
                posters.filter((poster) =>
                  filterPosters(poster, postersInTopicFilter)
                ).length -
                1
                ? "dark"
                : "primary"
            }
            disabled={
              currentPoster ===
              posters.filter((poster) =>
                filterPosters(poster, postersInTopicFilter)
              ).length -
              1
            }
            onClick={() => setCurrentPoster((current) => current + 1)}
          >
            Next abstract
          </Button>
        </div>
      </div>
      <div className="poster-with-buttons">
        <PosterGallery session={poster.id} poster={poster} />
      </div>
    </div>
  );
};

export default AllPosters;
